import { B2BDistributorIds, orderStatus, orderType, mapControl, userPermission, } from '@/constants';
import { addressBaseOrder } from '@/constants/global';
import { isAfterHours } from '@/utils/time';
/** 배달 상태별 가능한 이벤트
 * https://www.notion.so/barogohq/5c4809d6789a4bbaad04f7cf6d495903#4ea9c37c577d4d4995df1abc05f3759f
 */
// 접수
const ACCEPTED = [
    orderStatus.ACCEPTED.code,
    orderStatus.WORKER_REQUESTED.code,
];
// 배차
const ALLOCATED = [
    orderStatus.ALLOCATED.code,
    orderStatus.PICKUP_ARRIVED.code,
];
// 픽업
const PICKEDUP = [
    orderStatus.PICKUP_FINISHED.code,
];
// 완료
const FINISHED = [
    orderStatus.DROP_FINISHED.code,
];
// 취소
const CANCELED = [
    orderStatus.CANCELED.code,
];
export default class OrderPolicy {
    _store = undefined;
    static _user = {
        isDistributorUser: false,
        distributorId: '',
        hubId: '',
    };
    static initialize(store) {
        this.setUser(store);
    }
    static setUser(store) {
        this._user = {
            isDistributorUser: store.getters['user/isDistUser'],
            distributorId: store.getters['user/distributorId'],
            hubId: store.getters['user/hubId'],
        };
        this._store = store;
    }
    static user(next) {
        return next(this._user);
    }
    static isOwn(user, order) {
        if (user.isDistributorUser) {
            if (user.distributorId === order.distributorId) {
                return true;
            }
        }
        if (user.hubId === order.hubId) {
            return true;
        }
        return false;
    }
    /**
     * 주소기반 주문인지 판단
     */
    static isAddressBaseOrder(order) {
        if (addressBaseOrder.includes(order.hubId)) {
            return true;
        }
        return false;
    }
    /**
     * B2B 건 유무 확인
     * 주소기반은 총판이 b2b총판과 같기 때문에 b2b 판단 전에 주소기반인지를 판단해야 함
     * TODO : OrderPolicy 파일 호출시 순환 참조 발생. 구조 개선 필요..
     */
    static isB2B(order) {
        if (OrderPolicy.isAddressBaseOrder(order)) {
            return false;
        }
        if (B2BDistributorIds.O_B2B.includes(order.distributorId)) {
            return true;
        }
        return false;
    }
    /**
     * 배달 유형 노션 문서
     * 내 주문, b2b주문, 공유 주문 판단
     * https://www.notion.so/barogohq/67e01bcb8ab143cda056e4ec5b32e539#fd8db0ab2b5b4d1cbf2ad0291ef7792a
     */
    static getOrderType(order) {
        return this.user((user) => {
            if (this.isOwn(user, order)) {
                return orderType.OWN;
            }
            if (this.isB2B(order)) {
                return orderType.B2B;
            }
            return orderType.SHARED;
        });
    }
    /**
     * 배달 유형
     * 내 주문, b2b주문, 공유 주문 판단
     */
    static orderType(order, next) {
        return next(this.getOrderType(order));
    }
    /**
     * 배달취소
     */
    static isCancelAcceptedAvailable(order) {
        return this.orderType(order, (type) => {
            if (orderType.OWN === type) {
                const cancelAcceptedAvailableStatusCode = [
                    ...ACCEPTED,
                    ...ALLOCATED,
                    ...PICKEDUP,
                    ...FINISHED,
                ];
                return cancelAcceptedAvailableStatusCode.includes(order.statusCode);
            }
            return false;
        });
    }
    /**
     * 배차취소
     */
    static isCancelAllocatedAvailable(order) {
        const cancelAcceptedAvailableStatusCode = [
            ...ALLOCATED,
            ...PICKEDUP,
        ];
        return cancelAcceptedAvailableStatusCode.includes(order.statusCode);
    }
    /**
     * 재배차
     */
    static isReallocateAvailable(order) {
        const reallocateAvailableStatusCode = [
            ...ALLOCATED,
            ...PICKEDUP,
        ];
        return reallocateAvailableStatusCode.includes(order.statusCode);
    }
    /**
     * 선택된 주문들 접수가능여부
     */
    static areAcceptedAvailable(selectedOrderList) {
        return selectedOrderList
            .every(({ statusCode }) => (mapControl.orderStatusGroup[statusCode] === orderStatus.ACCEPTED.code));
    }
    /**
     * 선택된 주문들 재배차가능여부
     */
    static areReallocateAvailable(selectedOrderList) {
        if (this.acceptedType) {
            return false;
        }
        return selectedOrderList
            .every(({ statusCode }) => (mapControl.orderStatusGroup[statusCode] !== orderStatus.ACCEPTED.code));
    }
    /**
     * 본사상담
     * [ order.distributorId == 'H0095' ] AND [ 배차, 픽업 상태일 때 ]
     * [기획문서] 노출여부 조건: https://www.notion.so/barogohq/10006bb56ac048efaf699e7bfaffd131?v=cf1ee365df33423782ec48f5e3cec874
     * @param {Object} order
     * @returns {boolean}
     */
    static isCustomerSupportAvailable(order) {
        const { B2BDistributorId } = B2BDistributorIds;
        if (order.distributorId === B2BDistributorId) {
            const customerSupportAvailableStatusCode = [
                ...ALLOCATED,
                ...PICKEDUP,
            ];
            return customerSupportAvailableStatusCode.includes(order.statusCode);
        }
        return false;
    }
    /**
     * 배차
     */
    static isAllocateAvailable(order) {
        const allocateAvailableStatusCode = [
            ...ACCEPTED,
        ];
        return allocateAvailableStatusCode.includes(order.statusCode);
    }
    /**
     * 도착지 주소 변경
     */
    static isChangeDropAddressAvailable(order) {
        return this.orderType(order, (type) => {
            if (orderType.OWN === type) {
                const changeDropAddressAvailableStatusCode = [
                    ...ACCEPTED,
                    ...ALLOCATED,
                    ...PICKEDUP,
                ];
                return changeDropAddressAvailableStatusCode.includes(order.statusCode);
            }
            const changeDropAddressAvailableStatusCode = [
                ...ALLOCATED,
                ...PICKEDUP,
            ];
            return changeDropAddressAvailableStatusCode.includes(order.statusCode);
        });
    }
    /**
     * 상점상품준비
     */
    static isPickupWishAtAvailable(order) {
        return this.orderType(order, (type) => {
            if (orderType.OWN === type) {
                const pickupWishAtAvailableStatusCode = [
                    ...ACCEPTED,
                    ...ALLOCATED,
                ];
                return pickupWishAtAvailableStatusCode.includes(order.statusCode);
            }
            return false;
        });
    }
    /**
     * 라이더 이동
     */
    static isPickupDelayWishAtAvailable(order) {
        const pickupDelayWishAtAvailableStatusCode = [
            ...ALLOCATED,
        ];
        return pickupDelayWishAtAvailableStatusCode.includes(order.statusCode);
    }
    /**
     * 대행료 변경
     */
    static isAdjustDeliveryAmountAvailable(order) {
        const isPermission = this._store?.getters['user/getterUserPermission']([
            userPermission.P_EDIT_DELIVERY_CHARGE,
        ]);
        return isPermission && this.orderType(order, (type) => {
            if (orderType.OWN === type) {
                const adjustDeliveryAmountAvailableStatusCode = [
                    ...ACCEPTED,
                    ...ALLOCATED,
                    ...PICKEDUP,
                ];
                return adjustDeliveryAmountAvailableStatusCode.includes(order.statusCode);
            }
            return false;
        });
    }
    /**
     * 라이더 지원금 변경 == 지사지원금 === 허브지원금
     * 허브옵션 허브지원금 + 배달대행료 수정 권한 + 라이더 지원금 권한
     */
    static isWorkerSupportAmountAvailable(order) {
        const isPermission = this._store?.getters['user/getterUserPermission']([
            userPermission.P_EDIT_DELIVERY_CHARGE,
            userPermission.P_UPDATE_WORKER_SUPPORT_AMOUNT,
        ], true);
        // 라이더 지원금 사용 가능 여부
        const isWorkerSupportAmount = order
            ?.hubConfigurationDTO
            ?.options
            ?.isWorkerSupportAmount;
        const isPossibleType = this.orderType(order, (type) => {
            if (orderType.OWN === type) {
                const workerSupportAmountAvailableStatusCode = [
                    ...ACCEPTED,
                    ...ALLOCATED,
                    ...PICKEDUP,
                ];
                return workerSupportAmountAvailableStatusCode.includes(order.statusCode);
            }
            return false;
        });
        return isPermission
            && isWorkerSupportAmount
            && isPossibleType;
    }
    /**
     * 접수 상태
     */
    static isAcceptedStatus(order) {
        return ACCEPTED.includes(order.statusCode);
    }
    /**
     * 배차 상태
     */
    static isAllocatedStatus(order) {
        return ALLOCATED.includes(order.statusCode);
    }
    /**
     * 픽업 상태
     */
    static isPickedUpStatus(order) {
        return PICKEDUP.includes(order.statusCode);
    }
    /**
     * 수행 상태
     */
    static isFulfillmentStatus({ statusCode }) {
        return [
            orderStatus.WORKER_REQUESTED.code,
            ...ALLOCATED,
            ...PICKEDUP,
        ].includes(statusCode);
    }
    /**
     * 완료 상태
     */
    static isFinishedStatus(order) {
        return FINISHED.includes(order.statusCode);
    }
    /**
     * 취소 상태
     */
    static isCanceledStatus(order) {
        return CANCELED.includes(order.statusCode);
    }
    /**
     * 도착지 주소 마스킹
     * 3시간 후부터 마스킹
     */
    static doMaskDropInfo(order) {
        if (this.isFinishedStatus(order)
            || this.isCanceledStatus(order)) {
            const { _finishedAt, _canceledAt } = order;
            // 완료 또는 취소 상태일 경우 3시간이 지났는지 검사
            return isAfterHours(_finishedAt, 3) || isAfterHours(_canceledAt, 3);
        }
        return false;
    }
    /**
     * 고객 전화번호 마스킹
     */
    static doMaskCustomerSafePhone(order) {
        if (this.isFinishedStatus(order)
            || this.isCanceledStatus(order)) {
            const { _finishedAt, _canceledAt } = order;
            // 완료 또는 취소 상태일 경우 3시간이 지났는지 검사
            return isAfterHours(_finishedAt, 3) || isAfterHours(_canceledAt, 3);
        }
        return false;
    }
    /**
     * 결제정보 변경
     */
    static isAdjustPaymentAvailable(order) {
        return this.orderType(order, (type) => {
            if (orderType.OWN === type) {
                return [
                    ...ACCEPTED,
                    ...ALLOCATED,
                    ...PICKEDUP,
                ].includes(order.statusCode);
            }
            return [
                ...ALLOCATED,
                ...PICKEDUP,
            ].includes(order.statusCode);
        });
    }
    /**
     * 내 소속 워커가 수행하고 있는지 여부
     * @param {Object} order
     * @param {string} order.distributorId
     * @param {string} order.allocatedHubId
     * @returns {boolean}
     */
    static isTheOrderToFulfillByMyWorker({ allocatedDistributorId, allocatedHubId, }) {
        return this.user(({ isDistributorUser, distributorId, hubId, }) => {
            if (isDistributorUser) {
                return distributorId === allocatedDistributorId;
            }
            return hubId === allocatedHubId;
        });
    }
    /**
     * 관제 대상 여부
     * @param {Object} order
     * @param {string} order.statusCode
     * @param {string} order.distributorId
     * @param {string} order.hubId
     * @param {string} order.allocatedDistributorId
     * @param {string} order.allocatedHubId
     * @returns {boolean}
     */
    static isTheOrderCaredForByMe({ statusCode, distributorId, hubId, allocatedDistributorId, allocatedHubId, }) {
        if (this.isFulfillmentStatus({
            statusCode,
        })) {
            return this.orderType({
                distributorId,
                hubId,
            }, (type) => {
                if (orderType.OWN === type) {
                    return true;
                }
                return this.isTheOrderToFulfillByMyWorker({
                    allocatedDistributorId,
                    allocatedHubId,
                });
            });
        }
        return true;
    }
}
